import React from 'react';
import styles from './purchaseForm.module.scss';
import classnames from 'classnames';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Button, FormControl, FormGroup } from 'react-bootstrap';
import {
  PurchaseForm,
  getCurrencyOptions,
  getTimeScaleOptions,
} from 'v2/utils/dataProductsUtils';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import Select from 'v2/form/Select/Select';
import TextCreateDropDownPairFilter from 'v2/Filters/TextCreateDropDownPairFilter/TextCreateDropDownPairFilter';
import { TranslationKeys } from 'translations';
import { useTranslation } from 'react-i18next';

const PurchasesForm = () => {
  const { t } = useTranslation();
  const {
    register,
    control,
    getValues,
    formState: { isValid },
  } = useFormContext<PurchaseForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    keyName: 'key',
    name: 'purchases',
  });
  return (
    <div>
      <div>
        {fields.map((field, index) => (
          <FormGroup
            data-cy="dataset-history-form-group"
            key={field.key}
            className={classnames(styles.fieldFormContainer, field.id)}
          >
            <div
              className={classnames(
                'd-flex',
                'flex-wrap',
                'flex-row justify-content-between',
                styles.fieldFormGroup
              )}
            >
              <div>
                <div className={classnames(styles.fieldForm)}>
                  <FormLabel label={`Purchase Date`} required={false} />
                  <FormControl
                    data-cy="purchase-date-input-field"
                    className={styles.inputText}
                    type="date"
                    autoComplete="off"
                    aria-label={`purchases.${index}.purchase_date`}
                    {...register(`purchases.${index}.purchase_date`)}
                  />
                </div>
                <div className={classnames(styles.fieldForm, 'mt-4')}>
                  <FormLabel label={`Opt-out Date`} required={false} />
                  <FormControl
                    data-cy="opt-out-date-input-field"
                    className={styles.inputText}
                    type="date"
                    aria-label={`purchases.${index}.opt_out_date`}
                    {...register(`purchases.${index}.opt_out_date`)}
                  />
                </div>
              </div>
              <div className={classnames(styles.fieldForm)}>
                <FormLabel label={`Renewal Date`} required={false} />
                <FormControl
                  data-cy="renewal-date-input-field"
                  className={styles.inputText}
                  type="date"
                  autoComplete="off"
                  aria-label={`purchases.${index}.renewal_date`}
                  {...register(`purchases.${index}.renewal_date`)}
                />
              </div>

              <div
                className={classnames(styles.fieldForm)}
                data-cy="cost-value-field-container"
              >
                <FormLabel label={`Cost`} required={false} />
                <TextCreateDropDownPairFilter
                  name={`purchases.${index}.cost`}
                  control={control}
                  type="number"
                  placeholder="0"
                  prefix="$"
                  options={getCurrencyOptions()}
                  defaultValue={getValues(`purchases.${index}.cost`)}
                />
              </div>
              <div
                data-cy="time-period-dropdown-container"
                className={classnames(styles.fieldForm, styles.timescaleField)}
              >
                <Select
                  register={register(`purchases.${index}.timescale`)}
                  required={true}
                  hasValue={Boolean(getValues(`purchases.${index}.timescale`))}
                  options={getTimeScaleOptions()}
                  aria-label={`purchases.${index}.timescale`}
                />
              </div>
              <div className="flex-grow-1">
                <FormLabel label={`Purchase Details`} required={false} />
                <textarea
                  data-cy="note-text-input-field"
                  className={classnames('form-control', styles.textArea)}
                  placeholder={`Who was this ${t(
                    TranslationKeys.dataProduct_one
                  )} purchased for (what department) and why was it purchased?`}
                  rows={4}
                  aria-label={`purchases.${index}.note`}
                  {...register(`purchases.${index}.note`)}
                />
              </div>
            </div>

            <FormControl
              type="hidden"
              {...register(`purchases.${index}.id`, { required: false })}
            />

            <Button
              className={classnames('mt-3', styles.btnRemove)}
              variant="secondary"
              onClick={() => {
                remove(index);
              }}
            >
              Remove
            </Button>
          </FormGroup>
        ))}
      </div>

      {fields.length === 0 && (
        <p className={classnames('m-0', styles.emptyText)}>
          You have no purchase history for this{' '}
          {t(TranslationKeys.dataProduct_one)}!
        </p>
      )}

      {fields.length < 50 && (
        <div
          className={classnames({
            'd-flex justify-content-center': fields.length === 0,
          })}
        >
          <Button
            variant="link"
            onClick={() => append({ cost: { unit: 'USD', value: '' } })}
            className={classnames('pl-0', styles.addField)}
            disabled={!isValid}
          >
            + Add another purchase
          </Button>
        </div>
      )}
    </div>
  );
};

export default PurchasesForm;
